import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Trash2, Plus, Tag, Copy } from "lucide-react";

export default function AdminBookingCodes() {
  const [codes, setCodes]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [newCode, setNewCode] = useState("");
  const [saving, setSaving]   = useState(false);
  const [deleting, setDeleting] = useState(null);

  const load = () => {
    setLoading(true);
    api.getBookingCodes()
      .then((r) => setCodes(r.data.codes))
      .catch(() => toast.error("Failed to load booking codes"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    const code = newCode.trim().toUpperCase();
    if (!code) return;
    setSaving(true);
    try {
      await api.createBookingCode(code);
      toast.success(`Booking code "${code}" created`);
      setNewCode("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create code");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, code) => {
    if (!window.confirm(`Delete booking code "${code}"? Existing bookings using this code are not affected.`)) return;
    setDeleting(id);
    try {
      await api.deleteBookingCode(id);
      toast.success(`Code "${code}" deleted`);
      load();
    } catch (err) {
      toast.error("Failed to delete code");
    } finally {
      setDeleting(null);
    }
  };

  const handleCopy = (code) => {
    navigator.clipboard.writeText(code).then(() => toast.success(`"${code}" copied to clipboard`));
  };

  return (
    <div className="p-6 max-w-2xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>Booking Codes</h1>
        <p className="text-slate-500 text-sm mt-1">
          Codes unlock the group booking form. When a requester enters a valid code, meeting point is locked to
          <strong> Coffee@The Edge</strong> and group-specific fields are shown.
        </p>
      </div>

      {/* Add code form */}
      <form onSubmit={handleAdd} className="flex gap-3 mb-8">
        <div className="relative flex-1">
          <Tag className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
          <Input
            data-testid="new-code-input"
            value={newCode}
            onChange={(e) => setNewCode(e.target.value.toUpperCase())}
            placeholder="Enter new code (e.g. DVC2025)"
            required
            className="pl-9 h-11 uppercase tracking-widest font-mono"
          />
        </div>
        <Button type="submit" data-testid="add-code-btn" disabled={saving || !newCode.trim()}
          className="h-11 px-6 bg-[#15803d] hover:bg-green-800 text-white rounded-lg font-semibold">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4 mr-1.5" />}
          {saving ? "Adding..." : "Add Code"}
        </Button>
      </form>

      {/* Codes list */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-7 h-7 animate-spin text-green-700" />
        </div>
      ) : codes.length === 0 ? (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-10 text-center" data-testid="no-codes-message">
          <Tag className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">No booking codes yet.</p>
          <p className="text-slate-400 text-sm mt-1">Add a code above to enable group bookings.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="codes-list">
          {codes.map((c) => (
            <div key={c.id} data-testid={`code-row-${c.code}`}
              className="flex items-center justify-between bg-white border border-slate-200 rounded-xl px-4 py-3 shadow-sm hover:shadow transition-shadow">
              <div className="flex items-center gap-3">
                <span className="font-mono font-bold text-green-800 tracking-wider text-base">{c.code}</span>
                <span className="text-slate-400 text-xs">{c.created_at ? new Date(c.created_at).toLocaleDateString("en-IE") : ""}</span>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="icon" title="Copy code" onClick={() => handleCopy(c.code)}
                  className="h-8 w-8 text-slate-400 hover:text-green-700 hover:bg-green-50">
                  <Copy className="w-3.5 h-3.5" />
                </Button>
                <Button variant="ghost" size="icon" data-testid={`delete-code-${c.id}`}
                  title="Delete code" onClick={() => handleDelete(c.id, c.code)}
                  disabled={deleting === c.id}
                  className="h-8 w-8 text-slate-400 hover:text-red-600 hover:bg-red-50">
                  {deleting === c.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
